/**
 * Caro pattern 8 — the rotating laser carousel (legacy Avoid! attack #8, family
 * `caro`; docs/legacy/AVOID_LEGACY_ANALYSIS.md §1.3).
 *
 * Legacy behaviour: a master spinner tweens in over ~2s, then rotates a FIXED 720°
 * over `carospeed` (8/6/6/4s by difficulty — only the spin *speed* changes, the
 * sweep is always two full turns), with 1/2/3/4 evenly-spaced beams by difficulty,
 * then tweens back out over ~2s. The beams pivot about the centre of the play area,
 * so the survivable space is the rotating wedge between adjacent beams.
 *
 * We model the whole attack as a single {@link import('../types').RotatingSegmentHazard}
 * centred on the viewport: the fly-in is the harmless TELEGRAPH, the 720° spin is the
 * lethal ACTIVE window, and `angVel` is chosen so the beams complete exactly two
 * turns over that window (faster tiers spin in less time, matching the legacy
 * carospeed ramp). Coordinates are VIEWPORT-RELATIVE.
 *
 * NOTE: pure core module — never import Phaser, React, or Firebase here.
 */
import type { Hazard, RotatingSegmentHazard } from '../types';
import type { Pattern, PatternContext } from './types';

/** Total rotation of the carousel — legacy fixed two full turns (720°). */
const CARO_TURNS = 2;

/** Beam length as a fraction of the viewport's larger side (reaches past the edges). */
const CARO_LENGTH_FRACTION = 1.15;

/** Per-beam collision width as a fraction of the smaller viewport side. */
const CARO_THICKNESS_FRACTION = 0.05;

/**
 * Beam count by difficulty level (legacy 1/2/3/4), capped at four. Beams are spread
 * evenly around the circle by the renderer/stepper from this count.
 */
function caroBeams(level: number): number {
  return Math.max(1, Math.min(4, level));
}

/** Build the rotating carousel for one caro attack. */
function makeCaro(ctx: PatternContext): Hazard[] {
  const { viewport, difficulty, rng, elapsedMs } = ctx;
  const { width, height } = viewport;

  // Fly-in tease is the telegraph; the 720° spin is the lethal window. Faster tiers
  // resolve the spin sooner (legacy carospeed 8/6/6/4s → here scaled off attackSpeed).
  const telegraphMs = difficulty.attackSpeedMs;
  const activeMs = difficulty.attackSpeedMs * 3;

  // angVel set so the beams sweep exactly CARO_TURNS full turns over the active
  // window (rad/s). Sign picks spin direction from the seeded stream.
  const totalRad = CARO_TURNS * Math.PI * 2;
  const direction = rng() < 0.5 ? 1 : -1;
  const angVel = (direction * totalRad) / (activeMs / 1000);

  // Random initial offset so the survivable wedge isn't in the same place each time.
  const angle0 = rng() * Math.PI * 2;

  const beam: RotatingSegmentHazard = {
    id: 0,
    family: 'caro',
    kind: 'rotatingSegment',
    bornMs: elapsedMs,
    telegraphMs,
    activeMs,
    cx: width * 0.5,
    cy: height * 0.5,
    length: Math.max(width, height) * CARO_LENGTH_FRACTION,
    thickness: Math.min(width, height) * CARO_THICKNESS_FRACTION,
    angle0,
    angVel,
    beams: caroBeams(difficulty.level),
  };

  return [beam];
}

/** Attack #8 — rotating laser carousel (1–4 beams, two full turns). */
export const CARO: Pattern = {
  id: 'CARO',
  family: 'caro',
  spawn: makeCaro,
};
